import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';
import Helmet from 'react-helmet';
import _ from 'lodash';

import { makeCanonicalTagObj, makeHreflangTagArr } from '@/utils';
import Markdown from '../../components/Markdown';
import Placeholder from '../../components/Placeholder';
import { BLACK, BORDER_GRAY } from '../../styles/variables.style';
import media from '../../styles/media.style';
import QuickLinksSection from '../../components/QuickLinksSection';
import BreadCrumbMenu from '../../components/BreadCrumbMenus/';

const RecruitCover = styled.section`
  margin: 89px auto 30px;
  max-width: 1060px;
  width: 100%;
  padding: 0 20px;
  > div {
    text-align: center;
    font-family: HiraKakuStd-W4;
    font-size: 38px;
    letter-spacing: 4px;
    margin-bottom: 99px;
  }
  > .recruitTitle {
    margin-bottom: 39px;
  }
  h4 {
    text-align: center;
    font-family: HiraKakuStd-W3;
    font-size: 18px;
    font-weight: normal;
    font-stretch: normal;
    letter-spacing: 2px;
    padding-top: 60px;
  }
  ${media.tabletL`
    padding: 0 0;
  `};
`;

const SectionWrapper = styled.section`
  margin-bottom: 120px;
`;

const ListWrapper = styled.section`
  margin-bottom: 80px;
  &:last-child {
    margin-bottom: 0;
  }
`;

const ListSection = styled.ul`
  max-width: 1060px;
  border-top: 1px solid #000;
  border-bottom: 1px solid #9b9b9b;
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
  > li {
    display: flex;
    flex-direction: column;
    width: 100%;
    padding: 20px 0px 20px 0;
    color: #000;
    > dt {
      font-size: 16px;
      width: 240px;
      text-align: top;
      margin-bottom: 5px;
    }
    > dd {
      font-size: 14px;
      flex: 1;
      margin-bottom: 0;
      > p {
        margin-bottom: 0;
      }
    }
  }
  > li:not(:last-child) {
    border-bottom: 1px solid ${BORDER_GRAY.COMMON_3};
  }
  ${media.tabletL`
    > li {
      padding: 40px 113px 40px 0;
      flex-direction: row;
      > dt {
        font-size: 16px;
        font-weight: normal;
        margin-bottom: 0;
      }
    }
  `};
`;
const TitleBorder = styled.div`
  margin: 0 auto 60px;
  width: 40px;
  height: 2px;
  background-color: #000000;
`;
const ListTitle = styled.h5`
  font-family: HiraKakuStd-W6;
  font-size: 14px;
  letter-spacing: 1px;
  color: #000000;
  text-align: left;
`;

export const Text = styled.p`
  color: ${BLACK};

  ${media.tabletL`
    margin: 0 0 0 0;
  `};

  h1,
  h2,
  h3,
  p {
    font-size: 14px;
    line-height: 1.5em;
    font-weight: 400;
    margin: 0 0 20px 0;
    &:last-child {
      margin-bottom: 0;
    }
    word-break: break-word;
  }
  a {
    text-decoration: underline;
  }
`;

const Description = styled.div`
  text-align: center;
  margin-bottom: 40px;
  > p {
    font-size: 14px;
  }
`;

const AnchorLinkWrapper = styled.div`
  margin-top: -60px;
  text-align: right;
`;

const handlePageTop = () => {
  window.scrollTo(0, 0);
};

const keyToId = {
  newGradsQuickLinkLabel: 'new-grads-section',
  trainerQuickLinkLabel: 'trainer-section',
};

const RecruitPage = ({ data, locale }) => {
  const links = [];
  const anchor = [];
  Object.keys(keyToId).forEach(key => {
    if (data[key]) {
      links.push({ id: keyToId[key], label: data[key] });
      anchor.push(keyToId[key]);
    }
  });
  return (
    <React.Fragment>
      {_.get(data, 'metaData') && (
        <Helmet
          title={_.get(data.metaData, 'title')}
          link={[makeCanonicalTagObj(locale, '/recruit'), ...makeHreflangTagArr(data.metaData.region, '/recruit')]}
          meta={[
            {
              name: 'description',
              content: _.get(data.metaData, 'description'),
            },
            {
              name: 'keyword',
              content: _.get(data.metaData, 'keyword'),
            },
            // TODO: OGP タグは一旦保留 (2020年01月07日)
            // {
            //   property: 'og:url',
            //   content: _.get(data.metaData, 'url'),
            // },
            // {
            //   property: 'og:type',
            //   content: _.get(data.metaData, 'type'),
            // },
            // {
            //   property: 'og:title',
            //   content: _.get(data.metaData, 'title'),
            // },
            // {
            //   property: 'og:image',
            //   content: _.get(data.metaData, 'image.file.url'),
            // },
            // {
            //   property: 'og:description',
            //   content: _.get(data.metaData, 'description'),
            // },
            // {
            //   property: 'og:site_name',
            //   content: _.get(data.metaData, 'siteName'),
            // },
          ]}
        />
      )}
      {links.length > 0 && <QuickLinksSection links={links} />}
      <RecruitCover id="company-section">
        <div className="recruitTitle">{data.title || <Placeholder w="40%" h="38px" />}</div>
        {data.careerInformations.map((information, index) => (
          <SectionWrapper>
            <h4 id={anchor[index]}>{information.title || <Placeholder w="20%" h="20px" />}</h4>
            <TitleBorder />
            {information.description && <Markdown src={information.description} Component={Description} />}
            {typeof information.information !== 'undefined' && (
              <React.Fragment>
                {information.information.map(info => (
                  <ListWrapper key={info.entryId || info.key}>
                    <ListTitle>{info.title || <Placeholder w="25%" h="12px" />}</ListTitle>
                    <ListSection>
                      {info.contents.map(({ entryId, key, title, content }) => (
                        <li key={entryId || key}>
                          <dt>{title || <Placeholder w="25%" h="16px" />}</dt>
                          <dd>
                            {content ? <Markdown src={content} Component={Text} /> : <Placeholder w="100%" pb="20%" />}
                          </dd>
                        </li>
                      ))}
                    </ListSection>
                  </ListWrapper>
                ))}
                <AnchorLinkWrapper>
                  <a onClick={e => handlePageTop()}>Page top</a>
                </AnchorLinkWrapper>
              </React.Fragment>
            )}
          </SectionWrapper>
        ))}
      </RecruitCover>
      {locale && (
        <BreadCrumbMenu
          currentPath={`https://www.shiseido-professional.com/${locale}/recruit`}
          items={[
            {
              name: 'TOP',
              path: `/${locale}/`,
            },
            {
              name: 'ABOUT US',
              path: `/${locale}/about/`,
            },
            {
              name: 'RECRUIT',
              path: null,
            },
          ]}
        />
      )}
    </React.Fragment>
  );
};

RecruitPage.defaultProps = {
  data: {
    careerInformations: [
      {
        key: 1,
        title: undefined,
        information: [
          {
            key: 1,
            title: undefined,
            contents: [
              { key: 1, title: undefined, content: undefined },
              { key: 1, title: undefined, content: undefined },
              { key: 1, title: undefined, content: undefined },
            ],
          },
        ],
      },
    ],
  },
};

RecruitPage.propTypes = {
  locale: PropTypes.string.isRequired,
  data: PropTypes.PropTypes.shape(),
};
export default RecruitPage;



// WEBPACK FOOTER //
// ./src/pages/RecruitPage/index.js